import { createApi } from '@reduxjs/toolkit/query/react';
import { urls } from '../../utils/helpers/ApiClient';
import axiosFetchBase from '../axiosFetchBase';
import dayjs from 'dayjs';

export const reservationsApi = createApi({
  reducerPath: 'reservationsApi',
  keepUnusedDataFor: 0,
  baseQuery: axiosFetchBase,
  endpoints: builder => ({
    getReservations: builder.query({
      query() {
        return { url: urls.reservations};
      },
    }),

    getReservationsForOverview: builder.query({
      query: ({ month }) => {
        const params = {};
        if (month) {
          params.month = dayjs(month).isValid() ? dayjs(month).format('YYYY-MM') : month;
        }
        return {
          url: urls.reservationsForOverview,
          params,
        };
      },
    }),

    getReservationStateChangeByEmail: builder.query({
      query(req) {
        const { newState, token } = req ?? {};
        return {
          url: urls.reservationsChangeStateByEmail(newState, token),
        };
      },
    }),

    getReservationStateChangeInCms: builder.mutation({
      query({ id, newState }) {
        return {
          url: urls.reservationsChangeStateInCms(newState, id),
          method: 'PUT',
          data: { id, newState },
        };
      },
    }),

    confirmReservationSequence: builder.mutation({
      query(data) {
        return {
          url: `${urls.confirmReservationSequence}`,
          method: 'PUT',
          data,
        };
      },
    }),

    getReservation: builder.query({
      query(id) {
        return { url: urls.reservationDetail(id) };
      },
    }),

    saveReservation: builder.mutation({
      query(req) {
        if (req.id) {
          return {
            url: urls.reservationDetail(req.id),
            method: 'PUT',
            data: req,
          };
        } else {
          return {
            url: urls.reservations,
            method: 'POST',
            data: req,
          };
        }
      },
    }),

    deleteReservation: builder.mutation({
      query(id) {
        return {
          url: `${urls.reservations}/${id}`,
          method: 'DELETE',
        };
      },
    }),

    getReservationSequence: builder.query({
      query(id) {
        return { url: urls.reservationSequenceDetail(id) };
      },
    }),

    deleteReservationSequence: builder.mutation({
      query(id) {
        return {
          url: `${urls.reservationsSequence}/${id}`,
          method: 'DELETE',
        };
      },
    }),

    saveReservationSequence: builder.mutation({
      query(req) {
        if (req.id) {
          return {
            url: urls.reservationSequenceDetail(req.id),
            method: 'PUT',
            data: req,
          };
        } else {
          return {
            url: urls.reservationsSequence,
            method: 'POST',
            data: req,
          };
        }
      },
    }),

    getSubjects: builder.query({
      query() {
        return { url: urls.reservationsSubjects };
      },
    }),

    getCollidedSubjects: builder.mutation({
      query(data) {
        return {
          url: `${urls.reservationsCollidedSubjects}`,
          method: 'POST',
          data: data,
        };
      },
    }),

    getCollidedSubjectsForSequence: builder.mutation({
      query(data) {
        return {
          url: `${urls.reservationsSequencesCollidedSubjects}`,
          method: 'POST',
          data: data,
        };
      },
    }),

    // New: check time conflicts for a room
    getConflicts: builder.mutation({
      query(data) {
        return {
          url: `${urls.reservationsConflicts}`,
          method: 'POST',
          data,
        };
      },
    }),

    // New: check time conflicts for a room
    getSequenceConflicts: builder.mutation({
      query(data) {
        return {
          url: `${urls.reservationsSequencesConflicts}`,
          method: 'POST',
          data,
        };
      },
    }),

    // New: import reservations from CSV
    importReservations: builder.mutation({
      query(req) {
        const packedData = new FormData();
        packedData.append('file', req.body.file);
        return {
          url: urls.reservationsImport,
          method: 'POST',
          data: packedData,
        };
      },
    }),

  }),
});

export const {
  useGetReservationsQuery,
  useGetReservationsForOverviewQuery,
  useGetReservationStateChangeByEmailQuery,
  useGetReservationStateChangeInCmsMutation,
  useGetReservationQuery,
  useSaveReservationMutation,
  useDeleteReservationMutation,
  useGetReservationSequenceQuery,
  useDeleteReservationSequenceMutation,
  useSaveReservationSequenceMutation,
  useGetSubjectsQuery,
  useGetCollidedSubjectsMutation,
  useGetCollidedSubjectsForSequenceMutation,
  useGetConflictsMutation,
  useGetSequenceConflictsMutation,
  useImportReservationsMutation,
} = reservationsApi;
